import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  Subpage,
  ChartData,
  ChartType,
  ChartStatus,
} from 'types/Meteorology/Agro/TrackingForecast/DetailedView';

export interface MeteorologyAgroTrackingForecastDetailedViewControlPanel {
  subpage: Subpage,
  chartType: ChartType,
  groupId: number | undefined,
  multiGroupIds: number[],
  locationId: number | undefined,
  multiLocationIds: number[],
  timeFrequency: string | undefined,
  includeHistoricYears: boolean,
  includeObserved: boolean,
  variable: string | undefined,
  forecastDate: string,
  rounds: string[],
  triggerRequest: (() => void) | undefined;
  // Sinaliza que a abertura está preenchendo os defaults e vai auto-consultar
  // (#1822). Fica ligada durante toda a cascata (frequências → variáveis →
  // data-previsão) e só desliga quando a consulta final dispara — o gráfico usa
  // isso para mostrar o spinner o tempo todo, não só na requisição final.
  autoQuerying: boolean,
  chartData: ChartData,
}

const initialStatus: ChartStatus = { data: undefined, error: undefined, isFetching: false };
const initialChartData: ChartData = {
  locations: { line: { ...initialStatus }, heatmap: { ...initialStatus } },
  rounds: { line: { ...initialStatus }, heatmap: { ...initialStatus } },
};

const initialState: MeteorologyAgroTrackingForecastDetailedViewControlPanel = {
  subpage: 'locations',
  chartType: 'line',
  groupId: undefined,
  multiGroupIds: [],
  locationId: undefined,
  multiLocationIds: [],
  timeFrequency: undefined,
  includeHistoricYears: false,
  includeObserved: false,
  variable: undefined,
  forecastDate: '',
  rounds: [],
  triggerRequest: undefined,
  autoQuerying: false,
  chartData: initialChartData,
};

export const meteorologyAgroTrackingForecastDetailedViewControlPanelSlice = createSlice({
  name: 'meteorologyAgroTrackingForecastDetailedViewControlPanel',
  initialState,
  reducers: {
    resetCP: (state) => {
      Object.assign(state, {
        ...state,
        groupId: undefined,
        multiGroupIds: [],
        locationId: undefined,
        multiLocationIds: [],
        timeFrequency: undefined,
        includeHistoricYears: false,
        includeObserved: false,
        variable: undefined,
        forecastDate: '',
        rounds: [],
        autoQuerying: false,
        chartData: initialChartData,
      });
    },
    setSubpage: (state, action: PayloadAction<Subpage>) => {
      state.subpage = action.payload;
    },
    setChartType: (state, action: PayloadAction<ChartType>) => {
      state.chartType = action.payload;
    },
    setGroupId: (state, action: PayloadAction<number | undefined>) => {
      state.groupId = action.payload;
    },
    setMultiGroupIds: (state, action: PayloadAction<number[]>) => {
      state.multiGroupIds = action.payload;
    },
    setLocationId: (state, action: PayloadAction<number | undefined>) => {
      state.locationId = action.payload;
    },
    setMultiLocationIds: (state, action: PayloadAction<number[]>) => {
      state.multiLocationIds = action.payload;
    },
    setTimeFrequency: (state, action: PayloadAction<string | undefined>) => {
      state.timeFrequency = action.payload;
    },
    setIncludeHistoricYears: (state, action: PayloadAction<boolean>) => {
      state.includeHistoricYears = action.payload;
    },
    setIncludeObserved: (state, action: PayloadAction<boolean>) => {
      state.includeObserved = action.payload;
    },
    setVariable: (state, action: PayloadAction<string | undefined>) => {
      state.variable = action.payload;
    },
    setForecastDate: (state, action: PayloadAction<string>) => {
      state.forecastDate = action.payload;
    },
    setRounds: (state, action: PayloadAction<string[]>) => {
      state.rounds = action.payload;
    },
    setTriggerRequest: (state, action: PayloadAction<(() => void) | undefined>) => {
      state.triggerRequest = action.payload;
    },
    setAutoQuerying: (state, action: PayloadAction<boolean>) => {
      state.autoQuerying = action.payload;
    },
    setChartData: (
      state,
      action: PayloadAction<[subpage: Subpage, chartType: ChartType, status: ChartStatus]>,
    ) => {
      const [subpage, chartType, status] = action.payload;
      state.chartData[subpage][chartType] = status;
    },
  },
});

export const selectDVCPMATrackingForecastSubpage = (state: any) => state
  .meteorologyAgroTrackingForecastDetailedViewControlPanel.subpage;

export const selectDVCPMATrackingForecastChartType = (state: any) => state
  .meteorologyAgroTrackingForecastDetailedViewControlPanel.chartType;

export const selectDVCPMATrackingForecastLocationId = (state: any) => state
  .meteorologyAgroTrackingForecastDetailedViewControlPanel.locationId;

export const selectDVCPMATrackingForecastMultiLocationIds = (state: any) => state
  .meteorologyAgroTrackingForecastDetailedViewControlPanel.multiLocationIds;

export const selectDVCPMATrackingForecastGroupId = (state: any) => state
  .meteorologyAgroTrackingForecastDetailedViewControlPanel.groupId;

export const selectDVCPMATrackingForecastMultiGroupIds = (state: any) => state
  .meteorologyAgroTrackingForecastDetailedViewControlPanel.multiGroupIds;

export const selectDVCPMATrackingForecastTimeFrequency = (state: any) => state
  .meteorologyAgroTrackingForecastDetailedViewControlPanel.timeFrequency;

export const selectDVCPMATrackingForecastIncludeHistoricYears = (state: any) => state
  .meteorologyAgroTrackingForecastDetailedViewControlPanel.includeHistoricYears;

export const selectDVCPMATrackingForecastIncludeObserved = (state: any) => state
  .meteorologyAgroTrackingForecastDetailedViewControlPanel.includeObserved;

export const selectDVCPMATrackingForecastVariable = (state: any) => state
  .meteorologyAgroTrackingForecastDetailedViewControlPanel.variable;

export const selectDVCPMATrackingForecastForecastDate = (state: any) => state
  .meteorologyAgroTrackingForecastDetailedViewControlPanel.forecastDate;

export const selectDVCPMATrackingForecastRounds = (state: any) => state
  .meteorologyAgroTrackingForecastDetailedViewControlPanel.rounds;

export const selectDVCPMATrackingForecastChartData = (state: any) => state
  .meteorologyAgroTrackingForecastDetailedViewControlPanel.chartData;

export const selectDVCPMATrackingForecastTriggerRequest = (state: any) => state
  .meteorologyAgroTrackingForecastDetailedViewControlPanel.triggerRequest;

export const selectDVCPMATrackingForecastAutoQuerying = (state: any) => state
  .meteorologyAgroTrackingForecastDetailedViewControlPanel.autoQuerying;

export const {
  resetCP,
  setSubpage,
  setChartType,
  setGroupId,
  setMultiGroupIds,
  setLocationId,
  setMultiLocationIds,
  setTimeFrequency,
  setIncludeHistoricYears,
  setIncludeObserved,
  setVariable,
  setForecastDate,
  setRounds,
  setTriggerRequest,
  setAutoQuerying,
  setChartData,
} = meteorologyAgroTrackingForecastDetailedViewControlPanelSlice.actions;

export default meteorologyAgroTrackingForecastDetailedViewControlPanelSlice.reducer;
